import React, { useEffect, useState } from "react";
import { Button, Divider, Paper } from "@mantine/core";
import { toNumber } from "lodash";
import TextModeSelector from "./TextModeSelector";
import TextStyleInputs from "./TextStyleInputs";
import TextInputFields from "./TextInputFields";
import { notification } from "../../../../../../utils/notification";
import { EditorModesOptions, SelectedKeys } from "../../constants/types";
import { TextToInsert } from "../../constants/interface";

interface TextOptionsProps {
  px?: string;
  data: any;
  textInsertMode: EditorModesOptions;
  selectedText?: string;
  textToInsert: TextToInsert;
  setTextToInsert: React.Dispatch<React.SetStateAction<TextToInsert>>;
  setData: React.Dispatch<React.SetStateAction<any>>;
  setSelectedKeys: React.Dispatch<React.SetStateAction<SelectedKeys>>;
  setText: React.Dispatch<React.SetStateAction<any>>;
  t: (key: string) => string;
}

const defaultTextObj = { en: "", ro: "", ru: "" };
const isTextNumberLike = (value: any): boolean =>
  typeof value !== "object" && !isNaN(Number(value));

const getEmptyTextInsert = (): TextToInsert => ({
  rect: { x: "", y: "" },
  properties: { text: "", type: "text" },
  styles: {
    fontSize: undefined,
    writingMode: undefined,
    fill: undefined,
  },
});

const TextOptions: React.FC<TextOptionsProps> = ({
  px,
  data,
  textInsertMode,
  selectedText,
  textToInsert,
  setTextToInsert,
  setData,
  setSelectedKeys,
  setText,
  t,
}) => {
  const { text } = textToInsert.properties;
  const [isNumber, setIsNumber] = useState<boolean>(isTextNumberLike(text));

  const handleRadioChange = (value: string) => {
    setIsNumber(value === "number");
    setTextToInsert((prev) => ({
      ...prev,
      properties: { ...prev.properties, text: "", type: "text" },
    }));
  };

  const setTextToEdit = () => {
    const selectedTextData = data.properties.text[toNumber(selectedText)];
    if (selectedTextData) {
      setTextToInsert(selectedTextData);
    }
  };

  const handleUpdateDataText = () => {
    const index = toNumber(selectedText);
    if (isNaN(index)) {
      notification({
        type: "error",
        title: "Key of text null or undefined",
        message:
          "Could not retrieve selected text key from SVG <text> element.",
      });
      return;
    }

    setData((prevData: { properties: { text: any } }) => {
      const updatedText = [...prevData.properties.text];
      updatedText[index] = {
        ...updatedText[index],
        properties: {
          ...updatedText[index].properties,
          text: isNumber
            ? textToInsert.properties.text
            : { ...textToInsert.properties.text },
        },
        styles: { ...updatedText[index].styles, ...textToInsert.styles },
      };
      return { ...prevData, properties: { text: updatedText } };
    });

    setText((prevText: any) => {
      const updatedText = [...prevText];
      updatedText[index] = textToInsert;
      return updatedText;
    });

    setSelectedKeys((prev) => ({
      ...prev,
      selectedKeyText: "",
    }));
  };

  const updateTranslation = (lang: string, value: string) => {
    setTextToInsert((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        text: {
          ...(typeof prev.properties.text === "object"
            ? prev.properties.text
            : defaultTextObj),
          [lang]: value,
        },
      },
    }));
  };

  const updateText = (value: string | object) => {
    setTextToInsert((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        text: value,
      },
    }));
  };

  const setStyles = (styles: any) => {
    setTextToInsert((prev) => ({
      ...prev,
      styles,
    }));
  };

  useEffect(() => {
    if (!selectedText) return;

    if (textInsertMode.enabled) {
      setSelectedKeys((prev) => ({ ...prev, selectedKeyText: "" }));
      setTextToInsert(getEmptyTextInsert());
    } else {
      setTextToEdit();
    }
  }, [selectedText, textInsertMode.enabled]);

  useEffect(() => {
    setIsNumber(isTextNumberLike(textToInsert.properties.text));
  }, [textToInsert.properties.text]);

  return (
    <>
      <Divider my={10} />

      {!selectedText && (
        <TextModeSelector
          px={px}
          isNumber={isNumber}
          onChange={handleRadioChange}
          t={t}
        />
      )}

      <Paper px={px}>
        <TextStyleInputs
          styles={textToInsert.styles}
          setStyles={setStyles}
          t={t}
        />
        <TextInputFields
          isNumber={isNumber}
          text={text}
          updateText={updateText}
          updateTranslation={updateTranslation}
          t={t}
        />

        {selectedText && (
          <Button my={15} onClick={handleUpdateDataText}>
            {t("common.save")}
          </Button>
        )}
      </Paper>
    </>
  );
};

export default React.memo(TextOptions);
